import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../services/api";
import { Popover } from "./Popover";
import { GH_COLORS } from "./Style";

interface DecodedToken {
  username: string;
  sub: number;
  role: "admin" | "teacher" | "viewer";
  iat: number;
  exp: number;
}

interface UserProfile {
  id: number;
  username: string;
  role: "admin" | "teacher" | "viewer";
  photo: string | null;
  hasGoogle: boolean;
  googleName?: string | null;
}

interface TopMenuItemDef {
  label: string;
  category: string;
  icon: string;
}

function decodeJWT(token: string): DecodedToken | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const decoded = JSON.parse(atob(parts[1]));
    return decoded as DecodedToken;
  } catch (e) {
    return null;
  }
}

export default function TopNavBar({
  activeCategory,
  onCategoryChange,
}: {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}) {
  const [token, setToken] = useState<DecodedToken | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [schoolLogo, setSchoolLogo] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const loadProfile = async () => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (jwtToken) {
      const decoded = decodeJWT(jwtToken);
      setToken(decoded);

      if (decoded) {
        try {
          const data = await apiClient.getProfile();
          setProfile(data);
        } catch (err) {
          console.error("프로필 로드 실패:", err);
        }
      }
    }
  };

  useEffect(() => {
    loadProfile();

    // API에서 최신 settings 로드
    apiClient.getSettings().then((settings) => {
      if (settings.schoolName) setSchoolName(settings.schoolName);
      setSchoolLogo(settings.schoolLogoUrl ?? null);
    }).catch(() => {});

    const handleStorageChange = () => {
      loadProfile();
    };
    window.addEventListener("storage", handleStorageChange);

    const handleProfileUpdated = () => {
      console.log("프로필 업데이트 이벤트 감지");
      loadProfile();
    };
    window.addEventListener("profileUpdated", handleProfileUpdated);

    const handleSchoolNameChanged = (e: Event) => {
      const name = (e as CustomEvent<string>).detail;
      if (name) setSchoolName(name);
    };

    const handleSchoolLogoChanged = (e: Event) => {
      setSchoolLogo((e as CustomEvent<string | null>).detail);
    };

    window.addEventListener("schoolNameChanged", handleSchoolNameChanged);
    window.addEventListener("schoolLogoChanged", handleSchoolLogoChanged);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("profileUpdated", handleProfileUpdated);
      window.removeEventListener("schoolNameChanged", handleSchoolNameChanged);
      window.removeEventListener("schoolLogoChanged", handleSchoolLogoChanged);
    };
  }, []);

  const topMenuItems: TopMenuItemDef[] = [
    { label: "학생 관리", category: "student", icon: "👥" },
    { label: "수업 관리", category: "classroom", icon: "📚" },
    { label: "출결 관리", category: "attendance", icon: "🚪" },
    { label: "메시지 관리", category: "message", icon: "💬" },
    { label: "포인트 관리", category: "point", icon: "💳" },
  ];

  const adminMenuItems: TopMenuItemDef[] = [
    { label: "설정", category: "settings", icon: "⚙️" },
  ];

  const showTopMenu = token && (token.role === "admin" || token.role === "teacher");

  const isAuthRoute = location.pathname.startsWith("/login") || location.pathname.startsWith("/auth/");

  if (isAuthRoute) return null;
  if (!token || !profile) return null;

  const handleLogout = async () => {
    try {
      await apiClient.logout();
      apiClient.clearToken();
      navigate("/login");
    } catch (err) {
      console.error("로그아웃 실패:", err);
      navigate("/login");
    }
  };

  return (
    <nav
      style={{
        position: "relative",
        width: "100%",
        height: "auto",
        minHeight: 45,
        padding: "0 16px",
        backgroundColor: GH_COLORS.headerBg,
        borderBottom: `1px solid ${GH_COLORS.headerBorder}`,
        display: "flex",
        flexDirection: "column",
        userSelect: "none",
        WebkitUserSelect: "none",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "15px",
          height: 56,
          minHeight: 56,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
          <Link
            to={token?.role === "viewer" ? "/viewer" : "/student"}
            style={{
              color: GH_COLORS.fgOnEmphasis,
              textDecoration: "none",
              fontWeight: 700,
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
              userSelect: "none",
              WebkitUserSelect: "none",
            }}
            onDragStart={(e) => e.preventDefault()}
          >
            {schoolLogo && (
              <img
                src={schoolLogo}
                alt="학원 로고"
                style={{
                  height: "32px",
                  maxWidth: "32px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  backgroundColor: "#ffffff",
                  padding: "2px",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  pointerEvents: "none",
                }}
                draggable={false}
              />
            )}
            {schoolName}
          </Link>

          {showTopMenu && (
            <div style={{ display: "flex", gap: 12, marginLeft: 16 }}>
              {topMenuItems.map((item) => {
                const isActive = activeCategory === item.category;
                return (
                  <button
                    key={item.category}
                    onClick={() => onCategoryChange(item.category)}
                    style={{
                      background: "none",
                      border: "none",
                      color: GH_COLORS.fgOnEmphasis,
                      cursor: "pointer",
                      padding: "8px 12px",
                      fontSize: 14,
                      fontWeight: isActive ? 600 : 500,
                      borderBottom: isActive ? `3px solid ${GH_COLORS.accentBg}` : "3px solid transparent",
                      backgroundColor: isActive ? "rgba(79, 171, 255, 0.15)" : "transparent",
                      borderRadius: 4,
                      transition: "color 0.2s, background-color 0.2s, border-color 0.2s",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "rgba(79, 171, 255, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                );
              })}
              {token?.role === "admin" && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 0, marginLeft: 12, paddingLeft: 12, borderLeft: `1px solid rgba(255,255,255,0.2)` }}>
                    {adminMenuItems.map((item) => {
                      const isActive = activeCategory === item.category;
                      return (
                        <button
                          key={item.category}
                          onClick={() => onCategoryChange(item.category)}
                          style={{
                            background: "none",
                            border: "none",
                            color: GH_COLORS.fgOnEmphasis,
                            cursor: "pointer",
                            padding: "8px 12px",
                            fontSize: 14,
                            fontWeight: isActive ? 600 : 500,
                            borderBottom: isActive ? `3px solid ${GH_COLORS.accentBg}` : "3px solid transparent",
                            backgroundColor: isActive ? "rgba(79, 171, 255, 0.15)" : "transparent",
                            borderRadius: 4,
                            transition: "color 0.2s, background-color 0.2s, border-color 0.2s",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = "rgba(79, 171, 255, 0.1)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <span>{item.icon}</span>
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <Popover
          reference={
            <button
              title="프로필 메뉴"
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                border: `1px solid rgba(255,255,255,0.3)`,
                padding: 0,
                cursor: "pointer",
                backgroundColor: "rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {profile?.photo ? (
                <img
                  src={profile.photo}
                  alt="프로필 사진"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    pointerEvents: "none",
                  }}
                  draggable={false}
                />
              ) : (
                <span style={{ fontSize: "18px", color: GH_COLORS.fgOnEmphasis }}>👤</span>
              )}
            </button>
          }
          placement="bottom"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(140,149,159,0.2)",
              padding: "8px 0",
              minWidth: 200,
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: `1px solid ${GH_COLORS.border}`,
                fontSize: 13,
                color: GH_COLORS.fgMuted,
              }}
            >
              <div style={{ fontWeight: 700, color: GH_COLORS.fgDefault, marginBottom: 4 }}>
                {profile?.username}
              </div>
              <div>
                {profile?.role === "admin"
                  ? "관리자"
                  : profile?.role === "teacher"
                    ? "선생님"
                    : "뷰어"}
              </div>
            </div>

            <Link
              to="/profile"
              onClick={() => setPopoverOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                color: GH_COLORS.fgDefault,
                textDecoration: "none",
                fontSize: 14,
                transition: "background-color 0.2s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <span>👤</span>
              <span>프로파일</span>
            </Link>

            <button
              onClick={() => {
                setPopoverOpen(false);
                handleLogout();
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                border: "none",
                backgroundColor: "transparent",
                color: GH_COLORS.btnDangerBg,
                textDecoration: "none",
                fontSize: 14,
                cursor: "pointer",
                transition: "background-color 0.2s",
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#fff5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <span>🔓</span>
              <span>로그아웃</span>
            </button>
          </div>
        </Popover>
      </div>

      {/* 메뉴는 상단 바 내부에 통합됨 */}
    </nav>
  );
}
